import { 
  createContext, 
  useContext, 
  useState, 
  useEffect, 
  useCallback, 
  type ReactNode, 
} from 'react'; 
import type { BookingRecord, TripStatus } from '../types'; 
import { bookings as seedBookings } from '../data/bookings'; 
import { 
  fetchBookings, 
  createBooking, 
  updateBooking, 
  updateBookingTripStatus, 
  assignDriverToBooking, 
  updateBookingPaymentStatus, 
  removeBookingFromBackend, 
  removeDriverFromBooking, 
  notifyCustomer, 
} from '../api/bookings/bookingApi'; 
import { USE_DASHBOARD_REAL_BACKEND } from '../api/config/config'; 
 
type BookingsContextType = { 
  records: BookingRecord[]; 
  isLoading: boolean; 
  error: string | null; 
  addBooking: (payload: any) => Promise<boolean>; 
  updateBooking: (id: string, payload: any) => Promise<boolean>; 
  setTripStatus: (id: string, trip: TripStatus) => Promise<void>; 
 assignDriver: (
    id: string,
    driverId: string,
    driverFare: string,
    meetingBoardUrl?: string,
    driverName?: string,
    includeDriverPhone?: boolean
  ) => Promise<boolean>;
  removeDriver: (id: string) => Promise<void>; 
  togglePayment: (id: string) => Promise<void>; 
  removeBooking: (id: string) => Promise<void>; 
  notifyCustomer: ( 
    id: string, 
    subject: string, 
    message: string 
  ) => Promise<boolean>; 
}; 
 
const BookingsContext = createContext<BookingsContextType | undefined>( 
  undefined 
); 
 
export const BookingsProvider = ({ 
  children, 
}: { 
  children: ReactNode; 
}) => { 
  const [records, setRecords] = useState<BookingRecord[]>([]); 
  const [isLoading, setIsLoading] = useState(true); 
  const [error, setError] = useState<string | null>(null); 
 
  useEffect(() => { 
    let mounted = true; 
 
    const loadBookings = async () => { 
      setIsLoading(true); 
      setError(null); 
 
      if (!USE_DASHBOARD_REAL_BACKEND) { 
        setRecords(seedBookings); 
        setIsLoading(false); 
        return; 
      } 
 
      const res = await fetchBookings(); 
 
      if (!mounted) return; 
 
      if (res.success && res.data) { 
        setRecords(res.data); 
      } else { 
        console.warn('API fetch failed.', res.message); 
        setError(res.message); 
        setRecords([]); 
      } 
 
      setIsLoading(false); 
    }; 
 
    loadBookings(); 
 
    return () => { 
      mounted = false; 
    }; 
  }, []); 
 
  const update = useCallback( 
    (id: string, patch: Partial<BookingRecord>) => { 
      setRecords((current) => 
        current.map((booking) => 
          booking.id === id 
            ? { ...booking, ...patch } 
            : booking 
        ) 
      ); 
    }, 
    [] 
  ); 
 
  const addBooking = useCallback(async (payload: any) => { 
    if (!USE_DASHBOARD_REAL_BACKEND) { 
      const newRecord: BookingRecord = { 
        id: `booking-${Date.now()}`, 
        reference: `SCB-${Math.floor( 
          10000 + Math.random() * 90000 
        )}`, 
        customer: { 
          name: `${payload.customer.firstName} ${payload.customer.lastName}`, 
          email: payload.customer.email, 
        }, 
        service: payload.trip.serviceType, 
        sourceNumber: payload.trip.sourceNumber || '', 
        sourceName: payload.trip.sourceName || '', 
        route: { 
          from: payload.trip.pickupLocation, 
          to: payload.trip.dropoffLocation, 
        }, 
        scheduledAt: `${payload.trip.pickupDate}T${payload.trip.pickupTime}:00`, 
        vehicle: 
          (Object.values(payload.vehicles)[0] as string) || 
          'Standard', 
        amountEur: 150, 
        driverAssigned: false, 
        payment: 'unpaid', 
        trip: 'upcoming', 
        passengers: 
          Number(payload.trip.passengers) || 0, 
        luggage: 
          Number(payload.trip.luggage) || 0, 
        customerPhone: 
          payload.customer.phoneNumber || '', 
        returnTrip: 
          payload.trip.journeyType === 'round-trip', 
        returnDateTime: 
          payload.trip.journeyType === 'round-trip' && 
          payload.trip.returnDate && 
          payload.trip.returnTime 
            ? `${payload.trip.returnDate}T${payload.trip.returnTime}:00` 
            : null, 
        paymentMethod: 
          payload.trip.paymentStatus || 'cash', 
        depositAmount: 0, 
        estimatedPrice: 150, 
        finalPrice: 0, 
        selectedOptions: 
          payload.customer.options || [], 
        status: 'pending', 
        notes: 
          payload.customer.specialRequirements || '', 
      }; 
 
      setRecords((current) => [ 
        newRecord, 
        ...current, 
      ]); 
 
      return true; 
    } 
 
    const res = await createBooking(payload); 
 
    if (res.success && res.data) { 
      const newRecord = res.data; 
 
      setRecords((current) => [ 
        newRecord, 
        ...current, 
      ]); 
 
      return true; 
    } 
 
    return false; 
  }, []); 
 
  const updateBookingRecord = useCallback( 
    async (id: string, payload: any) => { 
      if (!USE_DASHBOARD_REAL_BACKEND) { 
        setRecords((current) => 
          current.map((booking) => 
            booking.id === id 
              ? { 
                  ...booking, 
                  sourceNumber: 
                    payload.trip.sourceNumber || '', 
                  sourceName: 
                    payload.trip.sourceName || '', 
                  customer: { 
                    name: `${payload.customer.firstName} ${payload.customer.lastName}`.trim(), 
                    email: payload.customer.email, 
                  }, 
                  route: { 
                    from: payload.trip.pickupLocation, 
                    to: payload.trip.dropoffLocation, 
                  }, 
                  scheduledAt: `${payload.trip.pickupDate}T${payload.trip.pickupTime}:00`, 
                  passengers: 
                    Number(payload.trip.passengers) || 0, 
                  luggage: 
                    Number(payload.trip.luggage) || 0, 
                  customerPhone: 
                    payload.customer.phoneNumber || '', 
                  returnTrip: 
                    payload.trip.journeyType === 
                    'round-trip', 
                  returnDateTime: 
                    payload.trip.journeyType === 
                      'round-trip' && 
                    payload.trip.returnDate && 
                    payload.trip.returnTime 
                      ? `${payload.trip.returnDate}T${payload.trip.returnTime}:00` 
                      : null, 
                  paymentMethod: 
                    payload.trip.paymentStatus || 'cash', 
                  selectedOptions: 
                    payload.customer.options || [], 
                  notes: 
                    payload.customer.specialRequirements || 
                    '', 
                } 
              : booking 
          ) 
        ); 
 
        return true; 
      } 
 
      const res = await updateBooking(id, payload); 
 
      if (res.success && res.data) { 
        const updatedRecord = res.data; 
 
        setRecords((current) => 
          current.map((booking) => 
            booking.id === id 
              ? updatedRecord 
              : booking 
          ) 
        ); 
 
        return true; 
      } 
 
      return false; 
    }, 
    [] 
  ); 
 
  const setTripStatus = useCallback( 
    async (id: string, trip: TripStatus) => { 
      const result = await updateBookingTripStatus( 
        id, 
        trip 
      ); 
 
      if (result.success) { 
        update(id, { trip }); 
      } 
    }, 
    [update] 
  ); 
 
const assignDriver = useCallback(
  async (
    id: string,
    driverId: string,
    driverFare: string,
    meetingBoardUrl?: string,
    driverName?: string,
    includeDriverPhone?: boolean
  ): Promise<boolean> => {
    const result = await assignDriverToBooking(
      id,
      driverId,
      driverFare,
      meetingBoardUrl,
      includeDriverPhone
    );

    if (!result.success) {
      console.error('Driver assignment API failed:', result.message);
      return false;
    }

    const refreshed = await fetchBookings();

    if (refreshed.success && refreshed.data) {
      const refreshedBooking = refreshed.data.find(
        (booking) => booking.id === id
      );

      if (refreshedBooking) {
        update(id, {
          ...refreshedBooking,
          driverName:
            driverName?.trim() || refreshedBooking.driverName,
        });
      } else {
        update(id, {
          driverAssigned: true,
          driverId,
          driverName: driverName?.trim() || '',
          driverAmount: Number(driverFare || 0),
        });
      }
    } else {
      update(id, {
        driverAssigned: true,
        driverId,
        driverName: driverName?.trim() || '',
        driverAmount: Number(driverFare || 0),
      });
    }

    return true;
  },
  [update]
);
 
  const removeDriver = useCallback( 
    async (id: string) => { 
      console.log( 
        'REMOVE DRIVER CLICKED:', 
        id 
      ); 
 
      const result = 
        await removeDriverFromBooking(id); 
 
      console.log( 
        'REMOVE DRIVER RESULT:', 
        result 
      ); 
 
      if (result.success) { 
        update(id, { 
          driverAssigned: false, 
          driverId: undefined, 
          driverName: undefined, 
          driverAmount: undefined, 
        }); 
      } 
    }, 
    [update] 
  ); 
 
  const togglePayment = useCallback( 
    async (id: string) => { 
      const booking = records.find( 
        (candidate) => candidate.id === id 
      ); 
 
      if (booking) { 
        const newStatus = 
          booking.payment === 'paid' 
            ? 'unpaid' 
            : 'paid'; 
 
        update(id, { 
          payment: newStatus, 
        }); 
 
        await updateBookingPaymentStatus( 
          id, 
          newStatus 
        ); 
      } 
    }, 
    [records, update] 
  ); 
 
  const removeBooking = useCallback( 
    async (id: string) => { 
      const result = 
        await removeBookingFromBackend(id); 
 
      if (result.success) { 
        setRecords((current) => 
          current.filter( 
            (booking) => booking.id !== id 
          ) 
        ); 
      } 
    }, 
    [] 
  ); 
 
  const notifyCustomerRecord = useCallback( 
    async ( 
      id: string, 
      subject: string, 
      message: string 
    ): Promise<boolean> => { 
      const result = await notifyCustomer( 
        id, 
        subject, 
        message 
      ); 
 
      return result.success; 
    }, 
    [] 
  ); 
 
  return ( 
    <BookingsContext.Provider 
      value={{ 
        records, 
        isLoading, 
        error, 
        addBooking, 
        updateBooking: updateBookingRecord, 
        setTripStatus, 
        assignDriver, 
        removeDriver, 
        togglePayment, 
        removeBooking, 
        notifyCustomer: 
          notifyCustomerRecord, 
      }} 
    > 
      {children} 
    </BookingsContext.Provider> 
  ); 
}; 
 
export const useBookingsState = () => { 
  const context = useContext( 
    BookingsContext 
  ); 
 
  if (context === undefined) { 
    throw new Error( 
      'useBookingsState must be used within a BookingsProvider' 
    ); 
  } 
 
  return context; 
};